import React, { useState } from 'react';
import styles from './RelatedCompaniesNetwork.module.scss';
import HeaderBodyBox from '../../../../../../common/HeaderBodyBox/HeaderBodyBox';
import { purpleOfficeIcon, purpleRedirectIcon, hollowBlackInfoIcon } from '../../../../../../assets/hostedassets';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { formatDateByFormat } from '../../../../../../utils/dateUtils';
import {
    DATE_FORMAT,
    RELATED_COMPANIES_NETWORK_TOOLTIP,
    AICA_REPORT_AVAILABLE_TOOLTIP,
} from '../../../../../../utils/constants/commonConstants';

interface RelatedCompany {
    gstin: string;
    orgId?: string;
    orgName: string;
}

interface CompaniesNetworkData {
    vendorFor?: RelatedCompany[];
    customerOf?: RelatedCompany[];
    lastUpdatedAt?: number;
}

interface RelatedCompaniesNetworkProps {
    data: CompaniesNetworkData | null;
    isLoading?: boolean;
    isPDFReport?: boolean;
}

const RelatedCompaniesNetwork: React.FC<RelatedCompaniesNetworkProps> = ({ data, isLoading = false, isPDFReport = false }) => {
    const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

    const hasData = data && (data.vendorFor?.length || data.customerOf?.length);

    if (!isLoading && !hasData) {
        return null;
    }

    const getRedirectUrl = (orgId: string) => {
        const baseUrl = window.location.origin;
        return `${baseUrl}/investor/app/portfolio/${orgId}?fetchType=all&page=1&type=scan&tab=aicaReport&selectedTab=overview`;
    };

    const renderTable = () => {
        const vendors = data?.vendorFor || [];
        const customers = data?.customerOf || [];

        // When loading, don't show anything
        if (isLoading) {
            return null;
        }

        // When data is available, show full table
        return (
            <div className={styles.TableContainer}>
                <table className={styles.Table}>
                    <tbody className={styles.TableBody}>
                        {vendors.length > 0 && (
                            <>
                                <tr className={styles.SectionHeaderRow}>
                                    <td className={styles.SectionHeader} colSpan={1}>
                                        Vendors for ({vendors.length})
                                    </td>
                                </tr>
                                {vendors.map((vendor, index) => {
                                    const hasOrgId = !!vendor.orgId;
                                    const rowId = `vendor-${index}`;
                                    return (
                                        <tr key={rowId} className={styles.TableBodyRow}>
                                            <td className={styles.TableBodyCell}>
                                                {hasOrgId ? (
                                                    <Tooltip
                                                        content={AICA_REPORT_AVAILABLE_TOOLTIP}
                                                        direction="top"
                                                        usePortal={true}
                                                        fitContent={true}
                                                    >
                                                        <a
                                                            href={getRedirectUrl(vendor.orgId!)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={styles.CompanyLink}
                                                            onMouseEnter={() => setHoveredRowId(rowId)}
                                                            onMouseLeave={() => setHoveredRowId(null)}
                                                        >
                                                            <div className={styles.CompanyNameCell}>
                                                                <img
                                                                    src={hoveredRowId === rowId ? purpleRedirectIcon : purpleOfficeIcon}
                                                                    alt="company"
                                                                    className={styles.CompanyIcon}
                                                                />
                                                                <span className={styles.CompanyName}>{vendor.orgName}</span>
                                                            </div>
                                                        </a>
                                                    </Tooltip>
                                                ) : (
                                                    <div className={styles.CompanyNameCell}>
                                                        <img src={purpleOfficeIcon} alt="company" className={styles.CompanyIcon} />
                                                        <span className={styles.CompanyName}>{vendor.orgName}</span>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </>
                        )}
                        {customers.length > 0 && (
                            <>
                                <tr className={styles.SectionHeaderRow}>
                                    <td className={styles.SectionHeader} colSpan={1}>
                                        Customers of ({customers.length})
                                    </td>
                                </tr>
                                {customers.map((customer, index) => {
                                    const hasOrgId = !!customer.orgId;
                                    const rowId = `customer-${index}`;
                                    return (
                                        <tr key={rowId} className={styles.TableBodyRow}>
                                            <td className={styles.TableBodyCell}>
                                                {hasOrgId ? (
                                                    <Tooltip
                                                        content={AICA_REPORT_AVAILABLE_TOOLTIP}
                                                        direction="top"
                                                        usePortal={true}
                                                        fitContent={true}
                                                    >
                                                        <a
                                                            href={getRedirectUrl(customer.orgId!)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={styles.CompanyLink}
                                                            onMouseEnter={() => setHoveredRowId(rowId)}
                                                            onMouseLeave={() => setHoveredRowId(null)}
                                                        >
                                                            <div className={styles.CompanyNameCell}>
                                                                <img
                                                                    src={hoveredRowId === rowId ? purpleRedirectIcon : purpleOfficeIcon}
                                                                    alt="company"
                                                                    className={styles.CompanyIcon}
                                                                />
                                                                <span className={styles.CompanyName}>{customer.orgName}</span>
                                                            </div>
                                                        </a>
                                                    </Tooltip>
                                                ) : (
                                                    <div className={styles.CompanyNameCell}>
                                                        <img src={purpleOfficeIcon} alt="company" className={styles.CompanyIcon} />
                                                        <span className={styles.CompanyName}>{customer.orgName}</span>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    const headerRight = isLoading ? (
        <div className={styles.LoaderMessage}>
            <LoadingSpinner color="#6021b3" height="15px" />
            <span>Fetching Data</span>
        </div>
    ) : data?.lastUpdatedAt ? (
        <div className={styles.LastUpdated}>Last Updated on {formatDateByFormat(data.lastUpdatedAt, DATE_FORMAT)}</div>
    ) : null;

    const title = (
        <div className={styles.TitleContainer}>
            <span>Related Entities in Network</span>
            <Tooltip content={RELATED_COMPANIES_NETWORK_TOOLTIP} direction="top" usePortal={true}>
                <img src={hollowBlackInfoIcon} alt="info" className={styles.InfoIcon} />
            </Tooltip>
        </div>
    );

    return (
        <HeaderBodyBox title={title} headerRight={headerRight} className={styles.RelatedCompaniesNetwork} isPDFReport={isPDFReport}>
            {(isLoading || hasData) && renderTable()}
        </HeaderBodyBox>
    );
};

export default RelatedCompaniesNetwork;
